import { TFunction } from 'i18next';
import * as React from 'react';
import { Button } from 'react-bootstrap';
import ReactTable, { Column, FinalState, RowInfo } from 'react-table';
import { setTableFilter } from '../../../actions/manageDocumentActions';
import { IcloudDocument, ItableFiltersReducer, Itile } from '../../../models';
import { TableUtil } from '../../common/TableUtil';
import { RowInfoDocument } from '../ManageDocumentLibrary';
import {
    getDocumentDisplayName,
    getDocumentMimeType,
    getLastModifiedDate,
    getVersionDisplayNumber,
    sortVersionsNewestFirst
} from '../utils/ManageDocumentUtils';
import FileTypeIcon from '../FileTypeIcon';
import useFiltersHeight from '../../common/useFiltersHeight';
import moment from 'moment';

// a version row annotated with its place in the history
interface IversionRow extends IcloudDocument {
    versionNumber: number;
    isLatestVersion: boolean;
}

interface Iprops {
    t: TFunction;
    currentTile: Itile;
    selectedCloudDocument: IcloudDocument;
    totalPages: number;
    setTableFilter: typeof setTableFilter;
    tableFilters: ItableFiltersReducer;
    documentTypeOptions: { [key: string]: any };
    goBack: () => void;
    newVersion: (parentDocumentID: string) => void;
    editDoc: (documentID: string) => void;
    downloadDocument: (selectedCloudDocument: IcloudDocument) => void;
}

interface Istate {
    selectedRow: any;
    columns: any[];
}

const ManageDocumentVersions: React.FC<Iprops> = (props: Iprops) => {
    const { t, selectedCloudDocument } = props;

    const mobileColumns = TableUtil.translateHeaders(
        [
            {
                Header: 'name',
                id: 'name',
                // latest version keeps the link blue; older versions read
                // as history (grayed via the .version-old row class)
                accessor: (doc: IversionRow) => {
                    return (
                        <>
                            <span className="mobile-label">NAME</span>
                            <span
                                style={
                                    doc.isLatestVersion
                                        ? { color: '#007BC3' }
                                        : undefined
                                }
                                className="mobile-description"
                            >
                                <FileTypeIcon
                                    mimeType={
                                        doc.mimeType ||
                                        props.selectedCloudDocument.mimeType
                                    }
                                />
                                {getDocumentDisplayName(
                                    {
                                        ...doc,
                                        // type from the parent document; a
                                        // version can carry a mismatched-case id
                                        cloudDocumentTypeID:
                                            props.selectedCloudDocument
                                                .cloudDocumentTypeID
                                    },
                                    props.documentTypeOptions
                                )}
                            </span>
                            <span className="mngDoc-version-chip">
                                {`V${doc.versionNumber}`}
                            </span>
                        </>
                    );
                }
            },
            {
                Header: 'documentDescription',
                id: 'description',
                // raw stored name for this specific version; same label/value
                // pair as the name cell so it grays out on older versions too
                accessor: (doc: IversionRow) => (
                    <>
                        <span className="mobile-label">
                            {t('manageDocument:description')}
                        </span>
                        <span className="mobile-description">
                            {doc.name || ''}
                        </span>
                    </>
                )
            },
            {
                Header: 'createDate',
                id: 'jobDate',
                // short fields share one row, matching the list cards
                accessor: (doc: IversionRow) => {
                    const jobNumber =
                        doc.jobNumber || props.selectedCloudDocument.jobNumber;
                    const date = moment(getLastModifiedDate(doc))
                        .local(true)
                        .format('DD-MMM-YY');
                    return (
                        <div className="mngDoc-cell-pair">
                            {jobNumber ? (
                                <div className="mngDoc-header-column">
                                    <p className="mngDoc-table-mobile-header">
                                        JOB #
                                    </p>{' '}
                                    <p className="mngDoc-table-value">
                                        {jobNumber}
                                    </p>
                                </div>
                            ) : null}
                            <div className="mngDoc-header-column">
                                <p className="mngDoc-table-mobile-header">
                                    DATE
                                </p>{' '}
                                <p className="mngDoc-table-value">{date}</p>
                            </div>
                        </div>
                    );
                }
            }
        ],
        props.t
    );

    // newest first, numbered like the desktop tree (latest = VN)
    const versionRows: IversionRow[] = sortVersionsNewestFirst(
        selectedCloudDocument.versions || []
    ).map((version, index, all) => ({
        ...version,
        versionNumber: getVersionDisplayNumber(version, all.length - index),
        isLatestVersion: index === 0
    }));

    const [state] = React.useState<Istate>({
        selectedRow: {},
        columns: mobileColumns
    });

    const onPageChange = (page: number) => {
        const newPage = page + 1;
        props.setTableFilter({ page: newPage });
    };

    const getDocumentType = () => {
        const id = props.selectedCloudDocument.cloudDocumentTypeID;

        if (id === undefined || id === null || id === '') {
            return '';
        }

        if (props.documentTypeOptions && props.documentTypeOptions[id]) {
            return props.documentTypeOptions[id].name;
        } else {
            return '';
        }
    };

    const getTdProps = (
        finalState: FinalState,
        rowInfo: RowInfoDocument | undefined,
        column: Column | undefined,
        instance: any
    ) => {
        if (rowInfo) {
            return {
                onClick: () => {
                    props.editDoc(rowInfo.original.id);
                }
            };
        }

        return {};
    };

    // size the versions table to whatever the header/buttons really occupy
    const screenRef = useFiltersHeight([]);

    return (
        <div className="manage-document-versions" ref={screenRef}>
            <div className="manage-version-container">
                <Button
                    className="close-button"
                    onClick={() => {
                        props.goBack();
                    }}
                >
                    {t(`measurementPoints:close`)}
                </Button>
                <Button
                    className="new-version-button"
                    bsStyle={'primary'}
                    onClick={() => {
                        props.newVersion(props.selectedCloudDocument.id);
                    }}
                >
                    {t(`manageDocument:newVersion`)}
                </Button>
            </div>

            <div className="download-doc-container">
                <Button
                    className="download-version-button"
                    onClick={() => {
                        props.downloadDocument(props.selectedCloudDocument);
                    }}
                >
                    Download
                </Button>
            </div>

            <div className="document-version-container">
                <span className="doc-name">
                    <FileTypeIcon
                        mimeType={getDocumentMimeType(
                            props.selectedCloudDocument
                        )}
                    />
                    {getDocumentDisplayName(
                        props.selectedCloudDocument,
                        props.documentTypeOptions
                    )}
                </span>
                <span className="doc-date">{getDocumentType()}</span>
            </div>

            <ReactTable
                data={versionRows}
                columns={state.columns}
                getTdProps={getTdProps}
                getTrProps={(
                    finalState: FinalState,
                    rowInfo: RowInfo | undefined
                ) =>
                    rowInfo &&
                    rowInfo.original &&
                    !rowInfo.original.isLatestVersion
                        ? { className: 'version-old' }
                        : {}
                }
                minRows={1}
                pageSize={versionRows.length >= 10 ? versionRows.length : 10}
                pages={props.totalPages}
                page={props.tableFilters.page - 1}
                showPageSizeOptions={false}
                className={`beacon-table -highlight ${props.currentTile.color}`}
                previousText={t('common:previous')}
                nextText={t('common:next')}
                onPageChange={onPageChange}
                sortable={false}
                multiSort={false}
                noDataText={t('common:noDataText')}
                resizable={false}
            />
        </div>
    );
};

export default ManageDocumentVersions;
